import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import InputText from "../../components/Forms/InputText/InputText";
import Logo from "../../components/Logo/Logo";
import { routes } from "../../shared/router/routes";
import {
    IAuthTokens,
    IPasswordSetup,
    IRequestError
} from "../../shared/interfaces/authentication.interface";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import Success from "./../../shared/assets/imgs/graphic_2.svg";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import Button from "../../components/Button/Button";
import {
    useFirstLogin,
    useRecoverPassword,
    useValidateCode,
    useValidateRecoveryCode
} from "../../shared/hooks/useAuthentication";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import Link from "../../components/Link/Link";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";

function PasswordSetup() {
    const { setAuthTokens, setIsAuthenticated } = useContext(AuthenticationContext);
    const { setEvent } = useContext(AnalyticsContext);
    const { translate } = useContext(TranslationContext);
    const { isLoading, setIsLoading } = useContext(LoadingContext);
    const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
    const [errorCode, setErrorCode] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [runOnce, setRunOnce] = useState<boolean>(false);
    const validateCode = useValidateCode();
    const validateRecoveryCode = useValidateRecoveryCode();
    const setFirstLogin = useFirstLogin();
    const recoverPassword = useRecoverPassword();
    const location = useLocation();
    const navigate = useNavigate();
    const [query] = useSearchParams();

    const passwordRequirements = useMemo(
        () => [
            {
                label: translate("common.password.required", "Required"),
                test: (v: string) => !v,
                display: false
            },
            {
                label: translate(
                    "common.password.error_ncharacters",
                    "Between 8 and 64 characters"
                ),
                test: (v: string) => v.length < 8 || v.length > 64,
                display: true
            },
            {
                label: translate("common.password.error_number", "At least one number"),
                test: (v: string) => !/[0-9]/.test(v),
                display: true
            },
            {
                label: translate(
                    "common.password.error_uppercase",
                    "At least one uppercase letter"
                ),
                test: (v: string) => !/[A-Z]/.test(v),
                display: true
            },
            {
                label: translate(
                    "common.password.error_special_character",
                    "At least one special character (!, @, #, $, %, ^, &, *)"
                ),
                test: (v: string) => !/[!@#$%^&*]/.test(v),
                display: true
            },
            {
                label: translate(
                    "common.password.error_lowercase",
                    "At least one lowercase letter"
                ),
                test: (v: string) => !/[a-z]/.test(v),
                display: true
            }
        ],
        [translate]
    );

    const form = useFormik({
        initialValues: {
            password: "",
            confirmPassword: ""
        },
        validationSchema: Yup.object().shape({
            password: Yup.string().test("password-rules", "Invalid password", function (val) {
                const errors: string[] = [];
                const value = val ?? "";

                passwordRequirements.forEach((r) => {
                    if (r.test(value)) errors.push(r.label);
                });

                if (errors.length) {
                    return this.createError({
                        message: errors.join("/")
                    });
                }

                return true;
            }),
            confirmPassword: Yup.string()
                .required("Confirm password please")
                .test("confirm-password", "Passwords do not match", function (val) {
                    return this.parent.password === val;
                })
        }),
        validateOnChange: true,
        onSubmit: async (values: IPasswordSetup) => {
            const isActivation = location.pathname.match(routes.PASSWORD_RESET.path);

            const code = query?.get("code");
            if (!code) return;

            setIsLoading(true);
            let response = null;

            if (isActivation) {
                response = await setFirstLogin.mutateAsync({
                    activationCode: code,
                    password: values.password
                });
            } else {
                response = await recoverPassword.mutateAsync({
                    recoveryCode: code,
                    newPassword: values.password
                });
            }

            if (!response?.success) {
                const data = response?.data as IRequestError;
                setErrorCode(data?.code);
                setErrorMessage(data?.message);
                setIsLoading(false);
                return;
            }

            if (isActivation) {
                const data = response?.data as IAuthTokens;
                setAuthTokens(data);
                setIsAuthenticated(true);
                setEvent({
                    event_name: "app_register",
                    event_category: "auth",
                    event_action: "app_register"
                });
                setEvent({
                    event_name: "app_firstlogin",
                    event_category: "auth",
                    event_action: "app_firstlogin"
                });
                navigate(routes.HOME.path);
            } else {
                setEvent({
                    event_name: "app_resetpass",
                    event_category: "button_click",
                    event_action: "app_resetpass"
                });
                navigate(routes.LOGIN.path);
            }

            setIsLoading(false);
        }
    });

    const passwordLevels = ["", "Very Weak", "Weak", "Medium", "Good", "Very Good"];

    const getCurrentPasswordState = (): number => {
        return form.dirty
            ? Number(passwordLevels.length - 1 - (form.errors.password?.split("/").length ?? 0))
            : 0;
    };

    const checkRequirement = (label: string) => {
        return form.errors.password?.split("/").some((lb) => lb === label);
    };

    const validateActivationCode = useCallback(
        async (code: string) => {
            const response = await validateCode.mutateAsync(code);

            if (!response.success) {
                const data = response.data as IRequestError;
                const message = translate(
                    "page.password.invalid_activation_code",
                    "Invalid activation code"
                );
                setErrorCode(data.message);
                setErrorMessage(message);
            }
        },
        [setErrorCode, setErrorMessage, translate, validateCode]
    );

    const validateRecovery = useCallback(
        async (code: string) => {
            const response = await validateRecoveryCode.mutateAsync(code);

            if (!response.success) {
                const data = response.data as IRequestError;
                const message = translate(
                    "page.password.invalid_recovery_code",
                    "Invalid recovery code"
                );
                setErrorCode(data.message);
                setErrorMessage(message);
            }
        },
        [setErrorCode, setErrorMessage, translate, validateRecoveryCode]
    );

    useEffect(() => {
        if (isLoading || runOnce) return;
        const code = query.get("code");
        const isActivation = location.pathname.match(routes.PASSWORD_RESET.path);

        if (!code) return;

        if (isActivation) validateActivationCode(code);
        else validateRecovery(code);

        setRunOnce(true);
    }, [query, translate, runOnce, validateActivationCode, validateRecovery, location, isLoading]);

    return (
        <>
            {isSuccess === undefined && (
                <>
                    <Logo className="d-none d-md-block" />
                    <div className="d-flex flex-column gap-2 w-100">
                        <div className="d-flex flex-column gap-03 align-items-center">
                            <h1 className="text-h1-big-bd text-h1-big-bd-xl login_title w-100 text-md-center">
                                {translate("page.forgot_password.title", "Define password")}
                            </h1>
                        </div>
                        <form
                            className="form d-flex flex-column gap-12"
                            onSubmit={form.handleSubmit}>
                            {errorCode && (
                                <span className="mt-n02 color-red-600 text-bd4-md mb-n1">
                                    {translate(`auth.code.${errorCode}`, errorMessage)}
                                </span>
                            )}
                            <InputText
                                name="password"
                                label={translate("common.password", "Password")}
                                placeholder={translate(
                                    "form.placeholder.password",
                                    "Insert password"
                                )}
                                type="password"
                                customCSS={form.errors.password ? "has_error" : ""}
                                currentStep={getCurrentPasswordState()}
                                totalSteps={
                                    form.errors.password ? passwordLevels.length - 1 : undefined
                                }
                                stepLabel={passwordLevels[getCurrentPasswordState()]}
                                onChange={form.handleChange}
                            />
                            {form.errors.password && (
                                <div className="form_field mt-n1">
                                    <ul className="form_field-requirements">
                                        {passwordRequirements.map(
                                            (req, i) =>
                                                req.display && (
                                                    <li
                                                        className="text-bd3-md d-flex gap-02 align-items-start"
                                                        key={i}>
                                                        <i
                                                            className={`icon icon-16 ${
                                                                checkRequirement(req.label)
                                                                    ? "icon-Misuse color-red-600"
                                                                    : "icon-checkmark--filled color-green-600"
                                                            }`}></i>
                                                        {req.label}
                                                    </li>
                                                )
                                        )}
                                    </ul>
                                </div>
                            )}
                            <InputText
                                name="confirmPassword"
                                label={translate("form.confirm_password", "Confirm password")}
                                placeholder={translate(
                                    "form.placeholder.confirm_password",
                                    "Confirm password"
                                )}
                                type="password"
                                errors={
                                    form.submitCount > 0
                                        ? form.errors.confirmPassword?.split("/")
                                        : []
                                }
                                onChange={form.handleChange}
                            />
                            <div className="d-flex flex-column gap-1">
                                <Button
                                    disabled={!(form.dirty && form.isValid)}
                                    type="submit"
                                    variant="solid"
                                    customCss="btn_action mt-02 align-self-center"
                                    label={translate("common.save_changes", "Save changes")}
                                />
                                <Link
                                    to={routes.LOGIN.path}
                                    variant="link"
                                    customCss="login_link text-bd2-bd text-center">
                                    {translate("page.login.go_to_login", "Go to login")}
                                </Link>
                            </div>
                        </form>
                    </div>
                </>
            )}
            {!isSuccess && isSuccess !== undefined && (
                <div className="login_message d-flex flex-column justify-content-center align-items-center text-center gap-1">
                    <img src={Success} alt="" aria-hidden="true" className="mb-2" />
                    <h1 className="title text-h1-md px-md-5 mx-5">
                        {translate("common.generic_error", "There was an error")}
                    </h1>
                    <span className="text-bd1-rg message">
                        {translate("common.retry", "Please try again.")}
                    </span>
                    <button
                        onClick={() => setIsSuccess(undefined)}
                        className="button bg-green-500 color-neutrals-black text-bd2-bd w-100 mt-1">
                        {translate("page.login.change_password", "Change password")}
                    </button>
                </div>
            )}
            {isSuccess && (
                <div className="login_message d-flex flex-column justify-content-center align-items-center text-center gap-1">
                    <img src={Success} alt="" aria-hidden="true" className="mb-2" />
                    <h1 className="text-h1-md title px-md-5 mx-md-5">
                        {translate("page.login.password_success", "Password set successfully!")}
                    </h1>
                    <span className="text-bd1-rg message">
                        {translate(
                            "page.login.password_setup_success_description",
                            "Now please log in to continue."
                        )}
                    </span>
                    <Link
                        to={routes.LOGIN.path}
                        variant="solid"
                        customCss="button btn_action bg-green-500 color-neutrals-black text-bd2-bd mt-1">
                        {translate("common.login", "Login")}
                    </Link>
                </div>
            )}
        </>
    );
}

export default PasswordSetup;
